import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useKeycloak } from '../context/KeycloakContext';
import queueService from '../services/queueService';

// Queue priority options
const queuePriorities = ['low', 'medium', 'high', 'critical'];

// Queue types
const queueTypes = ['processing', 'maintenance', 'error', 'notification', 'custom'];

const Queues = () => {
  const { keycloak } = useKeycloak();
  const userInfo = keycloak.tokenParsed || {};
  const isAdmin = userInfo.realm_access?.roles?.includes('admin');
  
  // Queues state
  const [queues, setQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Queue dialog state
  const [queueDialog, setQueueDialog] = useState(false);
  const [queueAction, setQueueAction] = useState('');
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [queueForm, setQueueForm] = useState({
    name: '',
    type: 'processing',
    maxItems: 100,
    priority: 'medium'
  });
  
  // Delete confirmation dialog
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  
  // Load queues
  useEffect(() => {
    fetchQueues();
  }, []);
  
  // Fetch queues
  const fetchQueues = async () => {
    try {
      setLoading(true);
      const data = await queueService.getAll();
      setQueues(data);
    } catch (err) {
      console.error('Error fetching queues:', err);
      setError('Failed to load queues. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Open queue dialog
  const handleOpenQueueDialog = (action, queue = null) => {
    setQueueAction(action);
    
    if (action === 'edit' && queue) {
      setSelectedQueue(queue);
      setQueueForm({
        name: queue.name,
        type: queue.type,
        maxItems: queue.maxItems,
        priority: queue.priority
      });
    } else {
      setSelectedQueue(null);
      setQueueForm({
        name: '',
        type: 'processing',
        maxItems: 100,
        priority: 'medium'
      });
    }
    
    setQueueDialog(true);
  };
  
  // Close queue dialog
  const handleCloseQueueDialog = () => {
    setQueueDialog(false);
  };
  
  // Handle queue form change
  const handleQueueFormChange = (e) => {
    const { name, value, checked } = e.target;
    setQueueForm(prev => ({
      ...prev,
      [name]: name === 'active' ? checked : value
    }));
  };
  
  // Handle queue number input change
  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    setQueueForm(prev => ({
      ...prev,
      [name]: parseInt(value) || 0
    }));
  };
  
  // Submit queue form
  const handleSubmitQueueForm = async () => {
    try {
      setLoading(true);
      
      if (queueAction === 'create') {
        await queueService.create(queueForm);
      } else if (queueAction === 'edit' && selectedQueue) {
        await queueService.update(selectedQueue.id, queueForm);
      }
      
      handleCloseQueueDialog();
      fetchQueues();
    } catch (err) {
      console.error('Error submitting queue form:', err);
      setError('Failed to save queue. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Open delete confirmation
  const handleConfirmDelete = (queue) => {
    setSelectedQueue(queue);
    setDeleteConfirmOpen(true);
  };
  
  // Delete queue
  const handleDeleteQueue = async () => {
    try {
      setLoading(true);
      
      if (selectedQueue) {
        await queueService.delete(selectedQueue.id);
        fetchQueues();
      }
      
      setDeleteConfirmOpen(false);
    } catch (err) {
      console.error('Error deleting queue:', err);
      setError('Failed to delete queue. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Filter queues
  const filteredQueues = queues.filter(queue => {
    const matchesSearch =
      !searchQuery ||
      (queue.code && queue.code.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (queue.description && queue.description.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesSearch;
  });
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
          Code di lavoro
        </Typography>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Cerca per codice o descrizione..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                {isAdmin && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenQueueDialog('create')}
                  >
                    Aggiungi
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>

          {loading && (!queues || queues.length === 0) ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : !queues || queues.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="textSecondary">No queues configured</Typography>
        </Paper>
      ) : (
        <TableContainer component={(props) => <Paper elevation={0} variant="outlined" {...props} />}
          sx={{ mt: 2 }}>
          <Table sx={{ minWidth: 650 }} size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                <TableCell>Codice</TableCell>
                <TableCell>Descrizione</TableCell>
                <TableCell>Priorità</TableCell>
                <TableCell>Capacità</TableCell>
                <TableCell align="center">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredQueues.map((queue) => (
                <TableRow key={queue.id} hover sx={{ height: 48 }}>
                  <TableCell>
                    <Chip label={queue.code} color="primary"  variant="outlined" size="small" />
                  </TableCell>
                  <TableCell>{queue.description}</TableCell>
                  <TableCell>
                    <Chip
                      label={queue.priority}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{queue.capacity}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      edge="end"
                      color="primary"
                      onClick={() => handleOpenQueueDialog('edit', queue)}
                      sx={{ mr: 1 }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      color="error"
                      onClick={() => handleConfirmDelete(queue)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
        </Box>
      </Paper>
      
      
      
      {/* Queue Dialog */}
      <Dialog open={queueDialog} onClose={handleCloseQueueDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {queueAction === 'create' ? 'Add New Queue' : 'Edit Queue'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              required
              label="Queue Name"
              name="name"
              value={queueForm.name}
              onChange={handleQueueFormChange}
              sx={{ mb: 2 }}
            />
            
            <FormControl fullWidth required sx={{ mb: 2 }}>
              <InputLabel>Type</InputLabel>
              <Select
                name="type"
                value={queueForm.type}
                label="Type"
                onChange={handleQueueFormChange}
              >
                {queueTypes.map(type => (
                  <MenuItem key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth required sx={{ mb: 2 }}>
              <InputLabel>Priority</InputLabel>
              <Select
                name="priority"
                value={queueForm.priority}
                label="Priority"
                onChange={handleQueueFormChange}
              >
                {queuePriorities.map(priority => (
                  <MenuItem key={priority} value={priority}>
                    {priority.charAt(0).toUpperCase() + priority.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              required
              label="Max Items"
              name="maxItems"
              type="number"
              value={queueForm.maxItems}
              onChange={handleNumberChange}
              InputProps={{ inputProps: { min: 1 } }}
              sx={{ mb: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseQueueDialog}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSubmitQueueForm}
            disabled={!queueForm.name || !queueForm.type || !queueForm.priority || queueForm.maxItems < 1}
          >
            {loading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the queue "{selectedQueue?.name}"? 
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button 
            color="error" 
            variant="contained" 
            onClick={handleDeleteQueue}
          >
            {loading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Queues; 